/**
 * FRONT-761: what should happen to a queued mid-stream draft when the busy
 * flag transitions. Extracted as a pure function so the semantics are unit-
 * testable in the repo's node-only frontend test setup; the ChatInput effect
 * just executes the verdict.
 *
 * Semantics (settled on the ticket):
 *  - The queue only ever resolves on the busy -> idle edge; nothing fires
 *    mid-stream, ever.
 *  - A manual Stop is an intervention: the draft HOLDS (chip stays, user
 *    sends manually) rather than firing into whatever the user interrupted.
 *  - An errored turn also holds — never auto-send into a broken state.
 *  - An emptied draft cancels the queue; there is nothing to send.
 */
export type QueuedSendDecision = 'fire' | 'hold' | 'cancel' | 'none';

export function decideQueuedSend(input: {
  wasBusy: boolean;
  isBusy: boolean;
  queued: boolean;
  stopRequested: boolean;
  turnError: boolean;
  hasText: boolean;
}): QueuedSendDecision {
  if (!input.wasBusy || input.isBusy || !input.queued) return 'none';
  if (input.stopRequested || input.turnError) return 'hold';
  if (!input.hasText) return 'cancel';
  return 'fire';
}

/**
 * FRONT-1182: what the Enter key should do — send now, or snapshot into the
 * queue.
 *
 * Enter and the Send button used to disagree, and only in one state. Enter
 * deferred to the queue whenever a turn was active; Send only ever refused on
 * the local streaming flag. That divergence is harmless while a turn is
 * genuinely streaming — the queue resolves on the busy -> idle edge and the
 * message goes out. It DEADLOCKS when the thing making the turn "active" is a
 * question card waiting on the user: the queue fires on an edge, the only
 * thing that produces that edge is the card being answered, and the only thing
 * that can answer it is this composer's own submit. The fire condition depends
 * on itself, so the draft sits in "Queued — sends when the reply finishes"
 * forever and reads as swallowed. Typing the same words and clicking Send
 * worked, which is why it read as flaky rather than broken.
 *
 * `submitResolvesTurn` is the surface saying "a submit here settles the very
 * thing that is blocking" — the coding session passes it when a run is
 * genuinely parked on an open card that its own submit routes an answer into.
 * Everywhere else it is absent and the old behaviour is unchanged.
 *
 * Pure so the two input methods can be pinned to ONE table; the divergence
 * existed because each branch made its own decision inline.
 */
export type EnterAction = 'submit' | 'queue';

export function decideEnterAction(input: {
  turnActive: boolean;
  submitResolvesTurn: boolean;
}): EnterAction {
  if (!input.turnActive) return 'submit';
  return input.submitResolvesTurn ? 'submit' : 'queue';
}

/**
 * FRONT-1163: the one-time, mount-time verdict for a queued draft restored
 * from localStorage after a remount (every chat window remounts fresh per
 * chat — home.tsx / chats.tsx key the modal by item id). decideQueuedSend
 * only fires on a busy->idle EDGE it observes itself; a restored draft whose
 * turn already finished while the tab was away has no such edge left to
 * observe, so it needs its own verdict instead of silently never resolving.
 *
 *  - `pending` (the bgRun poll hasn't answered yet) always wins: deciding
 *    fire/hold against a guessed `false` turnActive would be reconciling
 *    against a guess, not the truth.
 *  - A still-running turn is left alone entirely — decideQueuedSend's
 *    busy->idle effect owns resolving it once it actually settles.
 *  - Once settled, mirrors decideQueuedSend's fire/hold split: clean -> fire,
 *    errored -> hold (never auto-send into a turn that came back broken).
 */
export type QueuedRestoreDecision = 'fire' | 'hold' | 'none';

export function decideQueuedRestore(input: {
  hasQueuedDraft: boolean;
  pending: boolean;
  turnActive: boolean;
  turnError: boolean;
}): QueuedRestoreDecision {
  if (input.pending || !input.hasQueuedDraft || input.turnActive) return 'none';
  return input.turnError ? 'hold' : 'fire';
}
